import { useState, useCallback, useRef } from 'react';
import { Strings} from '../../constants';
import { analyticsService, AnalyticsEvent } from '@cobu/analytics';
import { useOrganizedGroups, useSuggestGroupMutation } from './groupsHooks';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import type { Group } from '@cobu/api-client';
import type { UserInfo } from '@cobu/types';
import { logger } from '../../utils/logger';

const strings = Strings.get();

const BOT_SENDER_NAME = strings.groups.suggest.chat.robotName;

// Clean text by removing HTML entities, Unicode non-breaking spaces, and trimming whitespace
const cleanText = (text: string): string => {
  if (!text) return '';
  // First decode HTML entities, then handle Unicode non-breaking spaces
  return text
    .replace(/&nbsp;/gi, ' ') // HTML entity non-breaking space (case insensitive)
    .replace(/\u00A0/g, ' ') // Unicode non-breaking space (U+00A0)
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ') // Replace multiple consecutive whitespace with single space
    .trim();
};

const formatUserForAnalytics = (user: UserInfo | null) => {
  if (!user) return {};

  return {
    userId: user.uuid,
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    building: user.building?.name,
    roomNumber: user.roomNumber,
    displayName: user.displayName,
    isEmployee: user.isEmployee || false,
    verified: user.verified || false,
    verifiedPhone: user.verifiedPhone || false,
  };
};

interface SuggestGroupForm {
  title: string;
  description: string;
  comment: string;
  suggestingUser: string;
  building: string;
}

interface UseSuggestGroupConfig {
  onNavigateBack?: () => void;
}

type ChatDirection = 'incoming' | 'outgoing';

export interface ChatMessage {
  id: string;
  message: string;
  direction: ChatDirection;
  sender: string;
  createdAt: Date;
}

interface UseSuggestGroupReturn {
  messages: ChatMessage[];
  step: number;
  placeholder: string;
  showBackToApp: boolean;
  isSubmitting: boolean;
  form: SuggestGroupForm;
  onSend: (text: string) => void;
  onBackToApp: () => void;
  initializeConversation: () => void;
  shouldShowInputToolbar: boolean;
}

export const useSuggestGroup = ({
  // Default to a no-op to avoid coupling to a specific router here
  onNavigateBack = () => {},
}: UseSuggestGroupConfig = {}): UseSuggestGroupReturn => {

  const { allGroups } = useOrganizedGroups();
  const suggestGroupMutation = useSuggestGroupMutation();
  const user = useSelector((state: RootState) => state.auth.user);
  const initializedRef = useRef(false);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<SuggestGroupForm>({
    title: '',
    description: '',
    comment: '',
    suggestingUser: user?.uuid || '',
    building: user?.building?.uuid || '',
  });
  const [placeholder, setPlaceholder] = useState(strings.groups.suggest.chat.placeholders.groupName);
  const [showBackToApp, setShowBackToApp] = useState(false);

  const createBotMessage = useCallback((text: string, createdAt: Date): ChatMessage => ({
    id: `${Date.now()}-${Math.round(Math.random() * 1000000)}`,
    message: text,
    createdAt,
    direction: 'incoming',
    sender: BOT_SENDER_NAME,
  }), []);

  const sendResponse = useCallback((message: string) => {
    const newMessage: ChatMessage = createBotMessage(message, new Date());
    setMessages(previousMessages => [...previousMessages, newMessage]);
  }, [createBotMessage]);

  const submitSuggestion = useCallback(async (formData: SuggestGroupForm) => {
    try {
      // Clean all text fields before submission
      const cleanedTitle = cleanText(formData.title);
      const cleanedDescription = cleanText(formData.description);
      const cleanedComment = cleanText(formData.comment);
      
      await suggestGroupMutation.mutateAsync({
        title: cleanedTitle,
        description: cleanedDescription,
        comment: cleanedComment,
        suggestingUser: user?.uuid || '',
        building: user?.building?.uuid || '',
      });

      const userAnalyticsData = formatUserForAnalytics(user);
      const enhancedAnalyticsData = {
        ...userAnalyticsData,
        membership: false,
        needs: false,
        influence: true,
        connection: false,
        proposedgroupComment: cleanedComment,
        proposedgroupDescription: cleanedDescription,
        proposedgroupTitle: cleanedTitle,
        action: 'submitted',
        groupName: cleanedTitle,
      };

      /* ANALYTICS_DISABLED: USER_PROPOSE_GROUP ('user_propose_group')
        analyticsService.trackEvent(AnalyticsEvent.USER_PROPOSE_GROUP, enhancedAnalyticsData);
        */

    } catch (error) {
      logger.error('Failed to suggest group:', error);
      sendResponse(strings.groups.suggest.chat.errorResponse);
    }
  }, [suggestGroupMutation, sendResponse, user]);

  const determineResponse = useCallback((currentStep: number, currentForm: SuggestGroupForm) => {
    const responses = strings.groups.suggest.chat.responses;

    switch (currentStep) {
      case 1:
        sendResponse(responses.step1);
        break;
      case 2:
        sendResponse(responses.step2Excellent);
        sendResponse(responses.step2Question);
        sendResponse(responses.step2Note);
        break;
      case 3:
        sendResponse(responses.step3Question);
        break;
      case 4:
        submitSuggestion(currentForm);
        sendResponse(responses.step4Fabulous);
        sendResponse(strings.groups.suggest.success.message);
        break;
      default:
        break;
    }
  }, [sendResponse, submitSuggestion]);

  const handleUserResponse = useCallback((input: string) => {
    const trimmedInput = input.trim();
    const placeholders = strings.groups.suggest.chat.placeholders;

    switch (step) {
      case 1: {
        const existingGroup = allGroups?.find((group: Group) => group.name === trimmedInput);
        if (existingGroup) {
          sendResponse(strings.groups.suggest.errors.groupExists);
          return;
        }

        const newForm = { ...form, title: trimmedInput };
        setForm(newForm);
        setStep(2);
        setPlaceholder(placeholders.groupDescription);
        determineResponse(2, newForm);
        break;
      }
      case 2: {
        const newForm = { ...form, description: trimmedInput };
        setForm(newForm);
        setStep(3);
        setPlaceholder(placeholders.extraDetails);
        determineResponse(3, newForm);
        break;
      }
      case 3: {
        const newForm = { ...form, comment: trimmedInput };
        setForm(newForm);
        setStep(4);
        setShowBackToApp(true);
        determineResponse(4, newForm);
        break;
      }
      default:
        setStep(1.5);
        determineResponse(1.5, form);
        break;
    }
  }, [step, allGroups, form, sendResponse, determineResponse]);

  const onSend = useCallback((text: string) => {
    const trimmed = (text || '').trim();
    if (!trimmed) return;

    const outgoing: ChatMessage = {
      id: `${Date.now()}-${Math.round(Math.random() * 1000000)}`,
      message: trimmed,
      createdAt: new Date(),
      direction: 'outgoing',
      sender: user?.displayName || user?.firstName || 'You',
    };

    setMessages(previous => [...previous, outgoing]);
    handleUserResponse(trimmed);
  }, [handleUserResponse, user]);

  const onBackToApp = useCallback(() => {
    onNavigateBack();
  }, [onNavigateBack]);

  const initializeConversation = useCallback(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      determineResponse(1, form);
    }
  }, [determineResponse, form]);

  return {
    messages,
    step,
    placeholder,
    showBackToApp,
    isSubmitting: suggestGroupMutation.isPending,
    form,
    onSend,
    onBackToApp,
    initializeConversation,
    shouldShowInputToolbar: !showBackToApp,
  };
}; 